import React, { useCallback, useMemo, useState } from 'react'
import { addDays, differenceInCalendarDays } from 'date-fns'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { parseBackendDate } from '~/utils/timezone'
import { cm } from '~/utils/utils'
import type { ChartDataPoint } from './types'

// ---------------------------------------------------------------------------
// Dashboard-only display shaping: pad short ranges, floor at the Unix epoch
// calendar day. When displayEndDate is provided, extends the chart past today
// with zero-fill buckets. Exported for unit tests only.
// ---------------------------------------------------------------------------

/** Minimum number of day buckets drawn so very short ranges are not a single spike. */
export const MIN_WEEKLY_CHART_VISIBLE_BUCKETS = 30

const USAGE_CHART_MIN_INSTANT_MS = 0

export type UsageBarChartSeriesCalendar = 'utc' | 'local'

function ymdToInt(y: number, m: number, d: number): number {
  return y * 10000 + m * 100 + d
}

function getYmd(
  d: Date,
  calendar: UsageBarChartSeriesCalendar
): { y: number; m: number; day: number } {
  if (calendar === 'utc') {
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, day: d.getUTCDate() }
  }
  return { y: d.getFullYear(), m: d.getMonth() + 1, day: d.getDate() }
}

function lastDayOfMonth(year: number, month1to12: number): number {
  return new Date(Date.UTC(year, month1to12, 0)).getUTCDate()
}

export function chartLabelsNeedYearForDisambiguation(
  start: Date,
  end: Date,
  calendar: UsageBarChartSeriesCalendar
): boolean {
  const sa = getYmd(start, calendar)
  const ea = getYmd(end, calendar)
  const sdInt = ymdToInt(sa.y, sa.m, sa.day)
  const edInt = ymdToInt(ea.y, ea.m, ea.day)
  if (edInt <= sdInt) return false

  for (let month = 1; month <= 12; month++) {
    const yearsWithThatMonth = new Set<number>()
    for (let y = sa.y; y <= ea.y; y++) {
      const firstInt = ymdToInt(y, month, 1)
      const lastD = lastDayOfMonth(y, month)
      const lastInt = ymdToInt(y, month, lastD)
      if (!(lastInt < sdInt || firstInt > edInt)) {
        yearsWithThatMonth.add(y)
      }
    }
    if (yearsWithThatMonth.size >= 2) return true
  }
  return false
}

function addCalendarDay(d: Date, days: number, calendar: UsageBarChartSeriesCalendar): Date {
  if (calendar === 'utc') {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days))
  }
  return addDays(d, days)
}

function calendarDiffDays(
  later: Date,
  earlier: Date,
  calendar: UsageBarChartSeriesCalendar
): number {
  if (calendar === 'utc') {
    const ms = Date.UTC(later.getUTCFullYear(), later.getUTCMonth(), later.getUTCDate())
    const me = Date.UTC(earlier.getUTCFullYear(), earlier.getUTCMonth(), earlier.getUTCDate())
    return Math.round((ms - me) / 86400000)
  }
  return differenceInCalendarDays(later, earlier)
}

/** YYYYMMDD in the user's local calendar (matches date picker / getTodayForApi). */
function localYmdInt(d: Date): number {
  return ymdToInt(d.getFullYear(), d.getMonth() + 1, d.getDate())
}

function earliestLocalChartDay(): Date {
  const e = new Date(USAGE_CHART_MIN_INSTANT_MS)
  return new Date(e.getFullYear(), e.getMonth(), e.getDate())
}

/** Synthetic bucket labels: show the calendar date the bucket represents without timezone shift. */
function formatSyntheticChartAxisLabel(
  d: Date,
  includeYear: boolean,
  calendar: UsageBarChartSeriesCalendar
): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {})
  }
  if (calendar === 'utc') {
    const localEquiv = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    return new Intl.DateTimeFormat('en-US', opts).format(localEquiv)
  }
  return new Intl.DateTimeFormat('en-US', opts).format(d)
}

/**
 * Which API rows to keep: step along `stepCalendar` (UTC for billing buckets), cap at
 * `maxDate` (defaults to today's local date). Uses the step calendar's own date
 * representation so a UTC bucket whose calendar date is past `maxDate` is excluded.
 *
 * When `displayEndDate` is provided (custom date range extending past today), it
 * replaces today as the upper bound so future buckets are retained.
 */
function plausibleChartIndexBounds(
  n: number,
  anchorDate: Date,
  stepCalendar: UsageBarChartSeriesCalendar,
  now: Date,
  displayEndDate?: Date
): { firstIdx: number; lastIdx: number } | null {
  const minLocal = earliestLocalChartDay()
  const minLocalInt = localYmdInt(minLocal)
  const todayLocalInt = localYmdInt(new Date(now.getFullYear(), now.getMonth(), now.getDate()))
  const maxLocalInt = displayEndDate
    ? Math.max(todayLocalInt, localYmdInt(displayEndDate))
    : todayLocalInt

  const bucketDateInt = (day: Date): number => {
    const { y, m, day: d } = getYmd(day, stepCalendar)
    return ymdToInt(y, m, d)
  }

  let firstIdx: number | null = null
  for (let i = 0; i < n; i++) {
    const y = bucketDateInt(addCalendarDay(anchorDate, i, stepCalendar))
    if (y >= minLocalInt && y <= maxLocalInt) {
      firstIdx = i
      break
    }
  }
  if (firstIdx === null) return null

  let lastIdx = firstIdx
  for (let j = n - 1; j >= firstIdx; j--) {
    const y = bucketDateInt(addCalendarDay(anchorDate, j, stepCalendar))
    if (y >= minLocalInt && y <= maxLocalInt) {
      lastIdx = j
      break
    }
  }
  return { firstIdx, lastIdx }
}

export interface ExpandWeeklyChartForDisplayOptions {
  anchorDate: Date
  calendar: UsageBarChartSeriesCalendar
  now?: Date
  /** When the selected range extends past today, extend the chart with zero-fill buckets. */
  displayEndDate?: Date
}

export function expandWeeklyChartForDisplay(
  weeklyChart: ChartDataPoint[],
  options: ExpandWeeklyChartForDisplayOptions
): ChartDataPoint[] {
  const now = options.now ?? new Date()
  const { anchorDate, calendar: stepCalendar, displayEndDate } = options
  const n = weeklyChart.length

  if (n === 0) {
    if (!displayEndDate) return weeklyChart
    const totalDays = calendarDiffDays(displayEndDate, anchorDate, stepCalendar) + 1
    if (totalDays <= 0) return weeklyChart
    const includeYear = chartLabelsNeedYearForDisambiguation(
      anchorDate,
      displayEndDate,
      stepCalendar
    )
    const out: ChartDataPoint[] = []
    for (let i = 0; i < totalDays; i++) {
      const d = addCalendarDay(anchorDate, i, stepCalendar)
      out.push({
        label: formatSyntheticChartAxisLabel(d, includeYear, stepCalendar),
        messages_exchanged: 0,
        tasks_run: 0
      })
    }
    return out
  }

  const bounds = plausibleChartIndexBounds(n, anchorDate, stepCalendar, now, displayEndDate)
  if (bounds === null) {
    if (!displayEndDate) return []
    const totalDays = calendarDiffDays(displayEndDate, anchorDate, stepCalendar) + 1
    if (totalDays <= 0) return []
    const includeYear = chartLabelsNeedYearForDisambiguation(
      anchorDate,
      displayEndDate,
      stepCalendar
    )
    const out: ChartDataPoint[] = []
    for (let i = 0; i < totalDays; i++) {
      const d = addCalendarDay(anchorDate, i, stepCalendar)
      out.push({
        label: formatSyntheticChartAxisLabel(d, includeYear, stepCalendar),
        messages_exchanged: 0,
        tasks_run: 0
      })
    }
    return out
  }

  const { firstIdx, lastIdx } = bounds
  const sameSpan = firstIdx === 0 && lastIdx === n - 1
  const truncated = sameSpan ? weeklyChart : weeklyChart.slice(firstIdx, lastIdx + 1)
  const anchor = addCalendarDay(anchorDate, firstIdx, stepCalendar)
  const m = truncated.length

  const dataEnd = addCalendarDay(anchor, m - 1, stepCalendar)
  const chartEnd =
    displayEndDate && calendarDiffDays(displayEndDate, dataEnd, stepCalendar) > 0
      ? displayEndDate
      : dataEnd

  const totalBuckets = calendarDiffDays(chartEnd, anchor, stepCalendar) + 1

  if (totalBuckets >= MIN_WEEKLY_CHART_VISIBLE_BUCKETS) {
    if (totalBuckets === m) return truncated

    const includeYear = chartLabelsNeedYearForDisambiguation(anchor, chartEnd, stepCalendar)
    const out: ChartDataPoint[] = []
    for (let i = 0; i < totalBuckets; i++) {
      const d = addCalendarDay(anchor, i, stepCalendar)
      out.push({
        label:
          i < m
            ? truncated[i]!.label || formatSyntheticChartAxisLabel(d, includeYear, stepCalendar)
            : formatSyntheticChartAxisLabel(d, includeYear, stepCalendar),
        messages_exchanged: i < m ? truncated[i]!.messages_exchanged : 0,
        tasks_run: i < m ? truncated[i]!.tasks_run : 0
      })
    }
    return out
  }

  const paddedEnd = addCalendarDay(anchor, MIN_WEEKLY_CHART_VISIBLE_BUCKETS - 1, stepCalendar)
  const displayEnd = calendarDiffDays(paddedEnd, chartEnd, stepCalendar) > 0 ? paddedEnd : chartEnd

  const includeYear = chartLabelsNeedYearForDisambiguation(anchor, displayEnd, stepCalendar)
  const dayCount = calendarDiffDays(displayEnd, anchor, stepCalendar) + 1

  const out: ChartDataPoint[] = []
  for (let i = 0; i < dayCount; i++) {
    const d = addCalendarDay(anchor, i, stepCalendar)
    out.push({
      label:
        i < m
          ? truncated[i]!.label || formatSyntheticChartAxisLabel(d, includeYear, stepCalendar)
          : formatSyntheticChartAxisLabel(d, includeYear, stepCalendar),
      messages_exchanged: i < m ? truncated[i]!.messages_exchanged : 0,
      tasks_run: i < m ? truncated[i]!.tasks_run : 0
    })
  }
  return out
}

export function utcCalendarDateFromBackendIso(iso: string): Date | null {
  const d = parseBackendDate(iso)
  if (!d || isNaN(d.getTime())) return null
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

// ---------------------------------------------------------------------------

export type UsageChartBucket = 'day' | 'week' | 'month'

export interface UsageBarChartSeriesAnchor {
  anchorDate: Date
  calendar: UsageBarChartSeriesCalendar
  /** When the selected range extends past today, extend the chart with zero-fill buckets. */
  displayEndDate?: Date
}

interface UsageBarChartProps {
  data: ChartDataPoint[]
  className?: string
  /**
   * When set (dashboard), truncates/pads the daily series for readability.
   * Omit everywhere else so the chart shows `data` as-is.
   */
  seriesAnchor?: UsageBarChartSeriesAnchor | null
  /** When not `day`, skips daily expand/pad — bucket labels are already week/month aggregates. */
  chartBucket?: UsageChartBucket
}

// Generate smart Y-axis labels based on max value
const generateYAxisLabels = (maxValue: number): string[] => {
  if (maxValue <= 0) return ['10', '8', '6', '4', '2', '0']

  const niceNumbers = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000]
  const niceMax = niceNumbers.find(n => n >= maxValue) ?? Math.ceil(maxValue / 1000) * 1000

  const step = niceMax / 5
  const labels: string[] = []
  for (let i = 5; i >= 0; i--) {
    labels.push(String(Math.round(step * i)))
  }
  return labels
}

type BuildSmoothPathOptions = {
  /**
   * SVG y for value 0 (bottom of plot). Control points are clamped so the spline
   * cannot bulge below this line (non‑negative data).
   */
  clampYMax?: number
}

/**
 * Smooth cubic path (Catmull-Rom style). Optional `clampYMax` keeps curves from
 * dipping below the zero baseline when tension would otherwise overshoot.
 */
function buildSmoothPath(
  points: { x: number; y: number }[],
  closed: boolean,
  chartHeight?: number,
  opts?: BuildSmoothPathOptions
): string {
  if (points.length === 0) return ''
  if (points.length === 1) {
    const p = points[0]
    return closed
      ? `M ${p.x},${chartHeight} L ${p.x},${p.y} L ${p.x},${chartHeight} Z`
      : `M ${p.x},${p.y}`
  }

  const tension = 0.3
  const clampY = (y: number) => (opts?.clampYMax !== undefined ? Math.min(y, opts.clampYMax) : y)

  let d = `M ${points[0].x},${points[0].y}`

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(i + 2, points.length - 1)]

    const cp1x = p1.x + (p2.x - p0.x) * tension
    const cp1y = clampY(p1.y + (p2.y - p0.y) * tension)
    const cp2x = p2.x - (p3.x - p1.x) * tension
    const cp2y = clampY(p2.y - (p3.y - p1.y) * tension)

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`
  }

  if (closed && chartHeight !== undefined) {
    const lastPt = points[points.length - 1]
    const firstPt = points[0]
    d += ` L ${lastPt.x},${chartHeight} L ${firstPt.x},${chartHeight} Z`
  }

  return d
}

const MAX_X_LABELS = 7
/** Fewer ticks when labels include a full year — strings are wider */
const MAX_X_LABELS_WITH_YEAR = 5

/**
 * Compact axis text: "Mar 20, 2026" → "Mar 20 '26". Tooltip / hover still use full `label`.
 */
function abbreviateUsageChartXLabel(label: string): string {
  const t = label.trim()
  const m = t.match(/^(.+),\s*(\d{4})\s*$/)
  if (m) return `${m[1]}, '${m[2].slice(-2)}`
  return label
}

/**
 * Pick up to `max` evenly-spaced indices from an array of length `total`,
 * always including first and last.
 */
function pickEvenIndices(total: number, max: number): Set<number> {
  if (total <= max) {
    return new Set(Array.from({ length: total }, (_, i) => i))
  }
  const indices = new Set<number>()
  for (let i = 0; i < max; i++) {
    indices.add(Math.round((i * (total - 1)) / (max - 1)))
  }
  return indices
}

const UsageBarChartComponent: React.FC<UsageBarChartProps> = ({
  data,
  className,
  seriesAnchor,
  chartBucket = 'day'
}) => {
  const isCompactViewport = useCompactViewport()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const chartAreaRef = React.useRef<HTMLDivElement>(null)

  const chartData = useMemo(() => {
    if (chartBucket !== 'day' || !seriesAnchor?.anchorDate) {
      return data.length ? data : []
    }
    return expandWeeklyChartForDisplay(data, {
      anchorDate: seriesAnchor.anchorDate,
      calendar: seriesAnchor.calendar,
      displayEndDate: seriesAnchor.displayEndDate
    })
  }, [chartBucket, data, seriesAnchor])

  // Calculate max value from data (avoid Math.max(...arr): spreading huge arrays blows the stack)
  const maxDataValue = useMemo(() => {
    if (chartData.length === 0) return 10
    let max = 0
    for (const p of chartData) {
      const v = Math.max(p.messages_exchanged, p.tasks_run)
      if (v > max) max = v
    }
    return max
  }, [chartData])

  // Generate Y-axis labels based on data
  const yAxisLabels = useMemo(() => generateYAxisLabels(maxDataValue), [maxDataValue])
  const maxYValue = parseInt(yAxisLabels[0]) || 10

  const axisLabelsIncludeFullYear = useMemo(
    () => chartData.some(p => /,\s*\d{4}\s*$/.test(p.label.trim())),
    [chartData]
  )

  const xTickBudget = axisLabelsIncludeFullYear ? MAX_X_LABELS_WITH_YEAR : MAX_X_LABELS

  const visibleXLabelIndices = useMemo(() => {
    const set = pickEvenIndices(chartData.length, xTickBudget)
    return Array.from(set).sort((a, b) => a - b)
  }, [chartData.length, xTickBudget])

  // Chart dimensions (internal SVG coordinate space)
  const CHART_PADDING_TOP = 10
  const CHART_PADDING_BOTTOM = 10
  const CHART_HEIGHT = 200
  const SVG_WIDTH = 1000

  // Build SVG points for each series
  const { messagesPoints, tasksPoints, yClampMax } = useMemo(() => {
    if (chartData.length === 0) {
      return { messagesPoints: [], tasksPoints: [], yClampMax: 0 }
    }

    const usableHeight = CHART_HEIGHT - CHART_PADDING_TOP - CHART_PADDING_BOTTOM
    const yClampMax = CHART_PADDING_TOP + usableHeight

    const toPoints = (getValue: (p: ChartDataPoint) => number) =>
      chartData.map((point, i) => ({
        x: chartData.length === 1 ? SVG_WIDTH / 2 : (i / (chartData.length - 1)) * SVG_WIDTH,
        y: CHART_PADDING_TOP + usableHeight - (getValue(point) / maxYValue) * usableHeight
      }))

    return {
      messagesPoints: toPoints(p => p.messages_exchanged),
      tasksPoints: toPoints(p => p.tasks_run),
      yClampMax
    }
  }, [chartData, maxYValue])

  const smoothOpts = useMemo(() => ({ clampYMax: yClampMax }), [yClampMax])

  const messagesLinePath = useMemo(
    () => buildSmoothPath(messagesPoints, false, undefined, smoothOpts),
    [messagesPoints, smoothOpts]
  )
  const messagesAreaPath = useMemo(
    () => buildSmoothPath(messagesPoints, true, CHART_HEIGHT, smoothOpts),
    [messagesPoints, smoothOpts]
  )
  const tasksLinePath = useMemo(
    () => buildSmoothPath(tasksPoints, false, undefined, smoothOpts),
    [tasksPoints, smoothOpts]
  )
  const tasksAreaPath = useMemo(
    () => buildSmoothPath(tasksPoints, true, CHART_HEIGHT, smoothOpts),
    [tasksPoints, smoothOpts]
  )

  // Hover handler — find nearest data point based on mouse X
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (chartData.length === 0 || !chartAreaRef.current) return
      const rect = chartAreaRef.current.getBoundingClientRect()
      const relX = e.clientX - rect.left
      const relY = e.clientY - rect.top
      const pct = relX / rect.width
      const idx = Math.round(pct * (chartData.length - 1))
      const clamped = Math.max(0, Math.min(chartData.length - 1, idx))
      setHoveredIndex(clamped)
      setTooltipPos({ x: relX, y: relY })
    },
    [chartData]
  )

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null)
  }, [])

  const hoveredPoint = hoveredIndex !== null ? chartData[hoveredIndex] : null

  return (
    <div
      className={cm(
        'bg-[rgba(255,255,255,0.5)] border border-white rounded-[24px] flex flex-col gap-[10px]',
        isCompactViewport ? 'p-4' : 'p-[40px]',
        !className?.includes('w-') && 'w-[782px]',
        className
      )}
    >
      {/* Chart Header */}
      <div
        className={cm(
          'flex justify-end py-[8px]',
          isCompactViewport ? 'flex-col items-start gap-2' : 'items-center gap-[16px]'
        )}
      >
        <p className="flex-1 font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[#2E1F36]">
          Total Messages Exchanged
        </p>
        <div
          className={cm('flex', isCompactViewport ? 'flex-wrap gap-2' : 'items-center gap-[16px]')}
        >
          {/* Legend - Messages (ring style) */}
          <div className='flex items-center gap-[12px] px-[12px] py-[8px] border border-[#F1F4F9] rounded-[8px]'>
            <div className='w-[11px] h-[11px] rounded-full border-2 border-[#C4B5FD] bg-transparent' />
            <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[rgba(0,0,0,0.45)]">
              Messages Exchanged
            </p>
          </div>

          {/* Legend - Tasks (ring style) */}
          <div className='flex items-center gap-[12px] px-[12px] py-[8px] border border-[#F1F4F9] rounded-[8px]'>
            <div className='w-[11px] h-[11px] rounded-full border-2 border-[#8571F4] bg-transparent' />
            <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[rgba(0,0,0,0.45)]">
              Total Tasks Run
            </p>
          </div>
        </div>
      </div>

      {/* Plot + y-axis share fixed height; x-axis is below so it does not shrink the SVG */}
      <div className='flex flex-col'>
        <div className='flex h-[200px]'>
          {/* Y-Axis Labels (same 200px as plot — was misaligned when x-axis lived inside this row) */}
          <div className='w-[40px] h-full flex flex-col justify-between py-[10px] flex-shrink-0'>
            {yAxisLabels.map((label, index) => (
              <p
                key={index}
                className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[#99B2C6] text-right"
              >
                {label}
              </p>
            ))}
          </div>

          <div className='flex-1 min-w-0 ml-[14px] h-full relative'>
            <div
              ref={chartAreaRef}
              data-testid='usage-chart-plot'
              className='absolute inset-0'
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              {/* Grid Lines */}
              <div className='absolute inset-0 flex flex-col justify-between py-[10px] pointer-events-none'>
                {yAxisLabels.map((_, index) => (
                  <div key={index} className='w-full h-px bg-[#F1F4F9]' />
                ))}
              </div>

              {/* Area Chart SVG */}
              {chartData.length > 0 && (
                <svg
                  className='absolute inset-0 w-full h-full pointer-events-none'
                  viewBox={`0 0 ${SVG_WIDTH} ${CHART_HEIGHT}`}
                  preserveAspectRatio='none'
                >
                  <defs>
                    {/* Messages gradient (lighter purple) */}
                    <linearGradient id='messagesGradient' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor='#C4B5FD' stopOpacity='0.6' />
                      <stop offset='100%' stopColor='#C4B5FD' stopOpacity='0.05' />
                    </linearGradient>
                    {/* Tasks gradient (darker purple) */}
                    <linearGradient id='tasksGradient' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='0%' stopColor='#8571F4' stopOpacity='0.5' />
                      <stop offset='100%' stopColor='#8571F4' stopOpacity='0.05' />
                    </linearGradient>
                  </defs>

                  {/* Messages area fill */}
                  <path d={messagesAreaPath} fill='url(#messagesGradient)' />
                  {/* Messages line */}
                  <path
                    d={messagesLinePath}
                    fill='none'
                    stroke='#C4B5FD'
                    strokeWidth='2.5'
                    vectorEffect='non-scaling-stroke'
                  />

                  {/* Tasks area fill */}
                  <path d={tasksAreaPath} fill='url(#tasksGradient)' />
                  {/* Tasks line */}
                  <path
                    d={tasksLinePath}
                    fill='none'
                    stroke='#8571F4'
                    strokeWidth='2.5'
                    vectorEffect='non-scaling-stroke'
                  />
                </svg>
              )}

              {/* Vertical hover indicator line */}
              {hoveredIndex !== null && chartData.length > 1 && (
                <div
                  className='absolute top-0 bottom-0 w-px bg-[#8571F4] opacity-30 pointer-events-none'
                  style={{
                    left: `${(hoveredIndex / (chartData.length - 1)) * 100}%`
                  }}
                />
              )}

              {/* Tooltip */}
              {hoveredPoint && hoveredIndex !== null && (
                <div
                  className='absolute z-20 pointer-events-none'
                  style={{
                    left: tooltipPos.x,
                    top: tooltipPos.y - 8,
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  <div className='bg-white rounded-[8px] px-3 py-2 shadow-[0px_2px_8px_rgba(0,0,0,0.12)] border border-[#F1F4F9] whitespace-nowrap'>
                    <p className="font-['Geist',sans-serif] text-[12px] font-semibold text-[#2E1F36] mb-1">
                      {hoveredPoint.label}
                    </p>
                    <div className='flex items-center gap-1.5'>
                      <div className='w-2 h-2 rounded-full bg-[#C4B5FD]' />
                      <p className="font-['Geist',sans-serif] text-[11px] text-[rgba(0,0,0,0.65)]">
                        Messages: {hoveredPoint.messages_exchanged}
                      </p>
                    </div>
                    <div className='flex items-center gap-1.5 mt-0.5'>
                      <div className='w-2 h-2 rounded-full bg-[#8571F4]' />
                      <p className="font-['Geist',sans-serif] text-[11px] text-[rgba(0,0,0,0.65)]">
                        Tasks: {hoveredPoint.tasks_run}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* X-axis: label x-position matches data point x (i / (n-1)), not equal flex columns */}
        <div className='flex min-w-0'>
          <div className='w-[40px] flex-shrink-0' aria-hidden />
          <div className='flex-1 min-w-0 ml-[14px]'>
            <div
              data-testid='usage-bar-chart-x-axis'
              className='relative min-h-[64px] sm:min-h-[56px] w-full min-w-0 px-0.5'
            >
              {visibleXLabelIndices.map(idx => {
                const point = chartData[idx]
                const axisText = abbreviateUsageChartXLabel(point.label)
                const n = chartData.length
                const leftPct = n <= 1 ? 50 : (idx / (n - 1)) * 100
                return (
                  <div
                    key={idx}
                    className='absolute bottom-0 h-[52px] sm:h-[48px] w-0'
                    style={{ left: `${leftPct}%` }}
                  >
                    <p
                      title={point.label}
                      className={cm(
                        "absolute bottom-0 left-0 font-['Geist',sans-serif] font-medium text-[#99B2C6] whitespace-nowrap origin-bottom -translate-x-1/2 -rotate-[32deg]",
                        isCompactViewport
                          ? 'text-[10px] leading-[12px]'
                          : 'text-[11px] leading-[14px]'
                      )}
                    >
                      {axisText}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const UsageBarChart = React.memo(UsageBarChartComponent)
export default UsageBarChart
